import { observer } from "mobx-react-lite";
import { useTran } from "@/hooks";
import { Button } from "@/components/ui/button";
import { ArrowRight, ShieldCheck, Zap, Layers } from "lucide-react";
import { useTheme } from "@/hooks";
import { LanguageSwitcher } from "@/components/common";
import { ThemeSwitcher } from "@/components/layout";
import { cn } from "@/lib/utils";
import { useState, useEffect } from "react";
import { PATHS } from "@/lib/router";
import { useNavigate } from "react-router-dom";

export const SustainabilityPage = observer(() => {
  const { t } = useTran();
  const [elevated, setElevated] = useState(false);
  const themeStore = useTheme();
  const dark = themeStore.theme == "dark";
  const baseBg = "bg-background";
  const backdrop = "backdrop-blur supports-[backdrop-filter]:backdrop-blur-md";
  const borderClr = "border-border";
  const shadow = elevated ? "shadow-sm" : "";
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setElevated(window.scrollY > 2);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header
        className={cn(
          "sticky top-0 z-40 border-b transition-shadow",
          baseBg,
          backdrop,
          borderClr,
          shadow,
        )}
      >
        <div className="relative mx-auto flex h-14 w-full items-center justify-between px-4 lg:px-8">
          <img src="/logos/logo.svg" alt="NxStart" className="h-12 w-auto" />
          <div className="flex flex-none items-center gap-2">
            <LanguageSwitcher />
            <ThemeSwitcher />
          </div>
        </div>
      </header>

      {/* Hero section */}
      <main className="flex-1 flex flex-col items-center justify-center px-4 md:px-12 py-16 pb-24">
        <div className="max-w-4xl w-full">
          {/* Main headline with gradient text */}
          <h1 className="text-5xl md:text-7xl font-black dark:text-white text-black text-center mb-6 leading-tight tracking-tight">
            {t("Your Gateway to")}{" "}
            <span className="text-primary">
              {t("Evercomm's Sustainability Platform")}
            </span>
          </h1>

          {/* Description */}
          <p className="text-center dark:text-gray-400 text-gray-600 text-lg md:text-xl max-w-2xl mx-auto mb-10">
            {t(
              "NxStart is the entry point into Evercomm's sustainability platform. With a single sign-up, users gain access to tools like NXMap, NXPlan, NXOps, and PCAF to measure, plan, and report climate impact.",
            )}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            {/* <button className="bg-primary-600 hover:bg-primary-700 text-white font-bold px-8 py-4 rounded-lg flex items-center justify-center gap-2 transition-colors">
              Get Started
              <ArrowRight className="w-5 h-5" />
            </button> */}
            <Button
              type="button"
              size="xl"
              className="gap-2"
              variant={dark ? "default" : "secondary"}
              onClick={() => navigate(PATHS.LOGIN)}
            >
              {t("Get Started")}
              <ArrowRight className="w-5 h-5" />
            </Button>
          </div>

          {/* Features grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
            {[
              {
                icon: Zap,
                title: "Simplified onboarding process",
                desc: "Quick setup with minimal effort.",
              },
              {
                icon: ShieldCheck,
                title: "First-time compliance support",
                desc: "Guided help for new compliance users.",
              },
              {
                icon: Layers,
                title: "Large-scale sustainability management",
                desc: "Manage sustainability at scale easily.",
              },
            ].map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <div
                  key={idx}
                  className="bg-card border rounded-2xl p-6 dark:hover:border-primary hover:border-primary transition-colors"
                >
                  <Icon className="w-8 h-8 text-primary mb-4" />
                  <p className="dark:text-white text-black font-bold mb-2">
                    {t(feature.title)}
                  </p>
                  <p className="dark:text-gray-400 text-gray-600 text-sm">
                    {t(feature.desc)}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </main>

      <footer
        className={cn(
          "sticky bottom-0 z-40 border-t px-6 md:px-12 py-6 text-center text-sm",
          baseBg,
          backdrop,
          borderClr,
          "dark:text-gray-500 text-gray-800",
        )}
      >
        <p>
          {t("Powered by")}{" "}
          <span className="dark:text-gray-300 text-black font-semibold">
            Evercomm
          </span>{" "}
          · {t("Sustainable solutions for a better tomorrow")}
        </p>
      </footer>
    </div>
  );
});
